/**
 * GxPopover: the floating card behind GxTerm and "Why?". It renders in `document.body`, fixed to its anchor, so no card
 * `overflow: hidden` clips it; it opens below the anchor, flips above when the viewport is short, and stays inside the window.
 * Hover and focus belong to the caller: React events bubble through the portal to the anchor's wrapper.
 * On a phone or small tablet (the `phone` and `tabletS` bands) a card that can be closed (`onClose`) is a bottom sheet instead (GxSheet):
 * always modal, so `passive` is ignored, and a caller that pins the card on focus must not close it on blur there (`useSheetMode`).
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { isMobileBand, useBand } from "./breakpoints";
import { GxSheet } from "./GxSheet";

const GAP = 8;
const MARGIN = 8;
const PASSIVE_REST_MS = 4000;

interface Placement { readonly top: number; readonly left: number; readonly side: "below" | "above" }

function place(anchor: DOMRect, width: number, height: number, align: "start" | "end"): Placement {
  const below = anchor.bottom + GAP;
  // A sticky bar over the page's foot (Decide's) hides what lies under it, so the room below ends where the bar begins.
  const bar = document.querySelector<HTMLElement>("[data-testid='decide.next']")?.getBoundingClientRect().top;
  const floor = Math.min(window.innerHeight, bar !== undefined && bar > 0 ? bar : window.innerHeight);
  const fitsBelow = below + height <= floor - MARGIN;
  const side = fitsBelow || anchor.top - GAP - height < MARGIN ? "below" : "above";
  return {
    side,
    top: Math.max(MARGIN, side === "below" ? below : anchor.top - GAP - height),
    left: Math.max(MARGIN, Math.min(align === "end" ? anchor.right - width : anchor.left, window.innerWidth - width - MARGIN)),
  };
}

/** True where popovers are sheets. A caller that opens its card on focus and closes it on blur reads this, because the sheet takes focus. */
export const useSheetMode = (): boolean => isMobileBand(useBand());

export function GxPopover({ anchor, id, label, title, passive = false, align = "start", onClose, returnFocus = true, children }: {
  readonly anchor: RefObject<HTMLElement | null>; readonly id?: string; readonly label: string; readonly children: ReactNode;
  /** The sheet's heading on a phone (default `label`): the short name, where `label` is the longer accessible name. */
  readonly title?: string;
  /** Which edge of the anchor the card lines up with (default its left edge; the top bar's clock lines up on its right). */
  readonly align?: "start" | "end";
  /** A hover-only card lets the pointer through: it can cover the controls below its term, and the pointer on it would otherwise keep it open for good. */
  readonly passive?: boolean;
  /** Escape anywhere closes it and puts focus back on the anchor's control. */
  readonly onClose?: () => void;
  /** With `onClose`: focus goes back to the anchor's control unless the control itself opens the card on focus (a glossary term). Default true. */
  readonly returnFocus?: boolean;
}) {
  const sheet = useSheetMode() && onClose !== undefined;
  const ref = useRef<HTMLSpanElement>(null);
  const [at, setAt] = useState<Placement | null>(null);
  // A page popover sits under a drawer's overlay; one opened from a term inside a drawer or a takeover sits over it.
  const inDrawer = anchor.current?.closest(".gx-drawer, .gx-takeover") != null;
  useLayoutEffect(() => {
    if (sheet) return;
    const move = () => {
      const a = anchor.current?.getBoundingClientRect();
      const p = ref.current;
      if (a !== undefined && p !== null) setAt(place(a, p.offsetWidth, p.offsetHeight, align));
    };
    move();
    window.addEventListener("scroll", move, true);
    window.addEventListener("resize", move);
    return () => { window.removeEventListener("scroll", move, true); window.removeEventListener("resize", move); };
  }, [anchor, sheet, align]);
  // A hover-only card that the pointer merely rests on closes by itself after a few seconds and when the page scrolls, so it never sits over the tabs or the panel beside its term.
  useEffect(() => {
    if (sheet || !passive || onClose === undefined) return;
    const timer = window.setTimeout(onClose, PASSIVE_REST_MS);
    const away = () => onClose();
    window.addEventListener("scroll", away, true);
    return () => { window.clearTimeout(timer); window.removeEventListener("scroll", away, true); };
  }, [sheet, passive, onClose]);
  useEffect(() => {
    if (sheet || onClose === undefined) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      onClose();
      const control = anchor.current?.querySelector<HTMLElement>("button, [tabindex]");
      if (returnFocus && control !== undefined && control !== null && !anchor.current?.contains(document.activeElement)) control.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sheet, anchor, onClose, returnFocus]);
  if (sheet && onClose !== undefined) {
    // Focus comes back to the anchor's own control (the glossary term, the "?"), not to whatever held it when the sheet opened.
    const control = anchor.current?.querySelector<HTMLElement>("button, [tabindex]") ?? null;
    return <GxSheet title={title ?? label} onClose={onClose} invoker={control} {...(id === undefined ? {} : { id })}>{children}</GxSheet>;
  }
  return createPortal(
    <span ref={ref} id={id} role="dialog" aria-label={label} className="gx-popover" data-side={at?.side ?? "below"}
      style={{ position: "fixed", zIndex: inDrawer ? "calc(var(--gxs-z-drawer) + 3)" : "calc(var(--gxs-z-drawer) - 1)", pointerEvents: passive ? "none" : "auto", top: at?.top ?? 0, left: at?.left ?? 0, visibility: at === null ? "hidden" : "visible" }}>
      {children}
    </span>,
    document.body,
  );
}
